import type * as amplitudeBrowser from '@amplitude/analytics-browser'

import { getAmplitudeConfig, shouldBypassAuthForE2E } from '~/shell/config'

type AmplitudeClient = Pick<
  typeof amplitudeBrowser,
  'logEvent' | 'setUserId' | 'Identify' | 'identify'
>

/**
 * Nginx and Vite proxy this path because direct calls to Amplitude are commonly blocked by ad blockers.
 */
const SERVER_URL = '/amplitude/2/httpapi'

let client: AmplitudeClient | undefined
let loading = false
const queued: ((client: AmplitudeClient) => void)[] = []

/**
 * Queues calls while the SDK loads. Calls are dropped when Amplitude is not configured.
 */
const withClient = (call: (client: AmplitudeClient) => void): void => {
  if (client) {
    call(client)
  } else if (loading) {
    queued.push(call)
  }
}

/**
 * Loads Amplitude outside the entry chunk. SDK autocapture is limited to page views, sessions, and web vitals;
 * product events use the event library.
 */
export const initializeAmplitude = (): void => {
  if (shouldBypassAuthForE2E) {
    return
  }

  const config = getAmplitudeConfig()
  if (!config) {
    return
  }

  loading = true
  import('@amplitude/analytics-browser')
    .then((sdk) => {
      sdk.init(config.apiKey, undefined, {
        serverUrl: SERVER_URL,
        fetchRemoteConfig: false,
        autocapture: {
          attribution: false,
          pageViews: true,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
          pageUrlEnrichment: false,
          webVitals: true,
        },
      })
      client = sdk
      for (const call of queued.splice(0)) {
        call(sdk)
      }
    })
    .catch(() => {
      loading = false
      queued.length = 0
    })
}

export const amplitude = {
  logEvent: (
    eventName: string,
    eventProperties?: Record<string, unknown>,
  ): void => {
    withClient((sdk) => sdk.logEvent(eventName, eventProperties))
  },
}

/**
 * Sets the Amplitude user and unsets properties whose value is `undefined`.
 */
export const identifyAmplitudeUser = (
  userId: string,
  properties: Record<string, string | boolean | undefined>,
): void => {
  withClient((sdk) => {
    sdk.setUserId(userId)

    const identify = new sdk.Identify()
    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined) {
        identify.unset(key)
      } else {
        identify.set(key, value)
      }
    }
    sdk.identify(identify)
  })
}
